import { computed, inject, Injectable, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Report } from "@hydrantid/acm-client";
import { Store } from "@ngrx/store";
import { catchError, map, of, startWith, switchMap } from "rxjs";
import { CachedDataService } from "../services/cached-data/cached-data.service";
import { IconsService } from "../services/icons/icons.service";
import { AppUserSelectors } from "../modules/shared/app-user";
import { pagePermissions } from "../access-control/page-permissions/page-permissions.selectors";
import { Configuration, configurationToken } from "../../configuration";
import { getFeatureFlags } from "../access-control/feature-flags/feature-flags";
import { buildUserNavigationMenu } from "./build-user-navigation-menu";
import { buildAppNavigationMenu } from "./build-app-navigation-menu";
import { UserNavigationMenuItem } from "./navigation-menu.models";

interface AccountReports {
    accountId: string | undefined;
    reports: readonly Report[] | undefined;
}

/**
 * Builds the navigation menu shown to the curren logged-in user in the sidenav menu and also the browse page view.
 *
 * First we build the appNavigationMenu which defines every route the application supports.
 * Then we use the current logged-in users context to resolve access to appNavigationMenu items and build the userNavigationMenu.
 * Some items such as Reports, also need data from the user's current account before they can be shown.
 */
@Injectable({ providedIn: "root" })
export class NavigationMenuService {
    private readonly globalStore = inject(Store);
    private readonly cachedData = inject(CachedDataService);
    private readonly featureFlags = getFeatureFlags(inject<Configuration>(configurationToken));
    private readonly appNavigationMenu = buildAppNavigationMenu(inject(IconsService));

    private readonly pagePermissions = this.globalStore.selectSignal(pagePermissions);

    // The sole account ID is only present for users who have access to a single account.
    // It is used to determine whether the reports for that account should be shown in the menu.
    // If the user has access to multiple accounts, no menu items are shown for reports until the user selects an account.
    private readonly soleAccountId = this.globalStore.selectSignal(AppUserSelectors.soleAccountId);

    // Reports populate the dynamic Reports submenu. Pairing them with their account ID lets us hide
    // old-account links immediately while the new account's reports are loading.
    private readonly accountReports = toSignal(
        this.globalStore.select(AppUserSelectors.soleAccountId).pipe(
            switchMap((accountId) => {
                if (!accountId) {
                    return of({ accountId, reports: [] });
                }

                return this.cachedData.getReportsForAccount(accountId).pipe(
                    map((reports): AccountReports => ({ accountId, reports })),
                    startWith({ accountId, reports: undefined }),
                    catchError(() => of({ accountId, reports: [] })),
                );
            }),
        ),
        { initialValue: { accountId: undefined, reports: undefined } },
    );

    // Rebuild the user-visible menu whenever permissions, the selected account, or its reports change.
    // buildUserNavigationMenu filters the static definitions and evaluates dynamic items with this context.
    readonly userNavigationMenu: Signal<readonly UserNavigationMenuItem[]> = computed(() => {
        const pagePermissions = this.pagePermissions();
        if (!pagePermissions) {
            return [];
        }

        return buildUserNavigationMenu(this.appNavigationMenu, {
            pagePermissions,
            featureFlags: this.featureFlags,
            reports:
                this.accountReports().accountId === this.soleAccountId() ? this.accountReports().reports : undefined,
        });
    });
}
